import { useState, type ReactNode } from "react"
import { DotText } from "@/v2-backoffice-client/components/dot"
import CrmStatusLine, {
  CrmCheckedField,
  CrmCheckingDot,
  CrmStatusAction,
  CrmToggleMatchesButton,
} from "@/v2-backoffice-client/crm/CrmStatusLine"
import { MATCH_SOURCE_BACKOFFICE } from "@/v2-backoffice-client/crm/crm.constants"
import CustomerMatchList, {
  type CustomerMatchCandidate,
} from "@/v2-backoffice-client/customer/create/CustomerMatchList"

export type CustomerCreateMatchesProps = {
  /** The field the check hangs under (label + input). */
  children: ReactNode
  /** Something searchable has been typed — the check is live. */
  active: boolean
  loading: boolean
  /** False until the first answer for the current input arrived. */
  answered: boolean
  hubspotSearched: boolean
  candidates: CustomerMatchCandidate[]
  /** A Backoffice customer with the same org.nr — creation is blocked. */
  blockingDuplicate?: CustomerMatchCandidate
  /** The HubSpot company the operator picked to link on create. */
  selectedCompany: CustomerMatchCandidate | null
  onSelectCompany(candidate: CustomerMatchCandidate): void
  onClearSelection(): void
  onOpenCustomer(candidate: CustomerMatchCandidate): void
}

/**
 * The Name field as a combobox-shaped duplicate check: the verdict sits in a
 * status row attached under the input — "checking", "no duplicates",
 * "6 possible matches (6 in Backoffice)", "already a customer · open",
 * "linked on create · change" — with a chevron that drops the match list
 * down from it. Nothing here ever changes the form's layout.
 */
function CustomerCreateMatches(props: CustomerCreateMatchesProps) {
  const {
    children,
    active,
    loading,
    answered,
    hubspotSearched,
    candidates,
    blockingDuplicate,
    selectedCompany,
    onSelectCompany,
    onClearSelection,
    onOpenCustomer,
  } = props

  const [listOpen, setListOpen] = useState(false)

  const hasMatches = active && candidates.length > 0
  const matches = hasMatches ? (
    <CustomerMatchList
      candidates={candidates}
      selectedExternalCompanyId={
        selectedCompany?.ExternalCompanyId ?? undefined
      }
      onSelectCompany={(candidate) => {
        onSelectCompany(candidate)
        setListOpen(false)
      }}
      onOpenCustomer={onOpenCustomer}
    />
  ) : undefined

  return (
    <CrmCheckedField
      active={active}
      status={renderStatus()}
      matches={matches}
      matchesOpen={listOpen}
      onMatchesOpenChange={setListOpen}
    >
      {children}
    </CrmCheckedField>
  )

  function renderStatus() {
    const backofficeCount = candidates.filter(
      (candidate) => candidate.Source === MATCH_SOURCE_BACKOFFICE,
    ).length
    const hubspotCount = candidates.length - backofficeCount
    const separator = <span className="text-muted-foreground">·</span>
    const toggle = hasMatches && <CrmToggleMatchesButton open={listOpen} />

    if (blockingDuplicate) {
      return (
        <CrmStatusLine>
          <DotText variant="destructive" className="min-w-0 truncate">
            Already a customer: {blockingDuplicate.Name}
          </DotText>
          {separator}
          <CrmStatusAction
            onClick={() => {
              onOpenCustomer(blockingDuplicate)
            }}
          >
            open
          </CrmStatusAction>
          {toggle}
        </CrmStatusLine>
      )
    }

    if (selectedCompany) {
      return (
        <CrmStatusLine>
          <DotText variant="success-2" className="min-w-0 truncate">
            Linked on create: {selectedCompany.Name} (HubSpot)
          </DotText>
          {separator}
          <CrmStatusAction onClick={onClearSelection}>change</CrmStatusAction>
          {toggle}
        </CrmStatusLine>
      )
    }

    if (!answered) {
      return (
        <CrmStatusLine className="text-muted-foreground">
          <CrmCheckingDot />
          Checking Backoffice and HubSpot…
        </CrmStatusLine>
      )
    }

    if (candidates.length === 0) {
      return (
        <CrmStatusLine>
          <DotText variant="success-1" className="min-w-0 truncate">
            No duplicates{" "}
            {hubspotSearched
              ? "in Backoffice or HubSpot"
              : "in Backoffice · HubSpot not checked"}
          </DotText>
        </CrmStatusLine>
      )
    }

    const where = [
      backofficeCount > 0 && `${backofficeCount} in Backoffice`,
      hubspotCount > 0 && `${hubspotCount} in HubSpot`,
    ]
      .filter(Boolean)
      .join(", ")

    return (
      <CrmStatusLine>
        <DotText
          variant={backofficeCount > 0 ? "warning" : "info"}
          className="min-w-0 truncate"
        >
          {candidates.length === 1
            ? "1 possible match"
            : `${candidates.length} possible matches`}{" "}
          ({where})
        </DotText>
        {loading && <CrmCheckingDot />}
        {toggle}
      </CrmStatusLine>
    )
  }
}

export default CustomerCreateMatches
